import React from 'react'
import { hashHistory } from 'react-router'
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@mui/material'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import { formatDateLocale } from '../../../../../lib/utils.es6'
import { hasAuthourity } from '../../../../../lib/access.es6'
import { stageLabel } from '../../../../../components/RecentPolicies/Components/makerCheckStage'
import {
  APPROVABLE_STATUS,
  approvablePolicies,
  isPolicyApprovable,
  policyLabel,
  summaryMessage
} from '../../lib/groupPolicyApproval'
import {
  CardHeader,
  detailCard,
  labelStyle,
  StatusPill,
  safeText,
  valueStyle
} from './ui'

// QA asked for 20 by default; the rest are there for smaller and larger groups.
export const DEFAULT_ROWS_PER_PAGE = 20
export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100]

// Principal on a group member policy, read off the quotation's schedule
// entries the same way the recent-policies table does.
function principalName(policy) {
  if (
    !policy ||
    !policy.quotation ||
    !Array.isArray(policy.quotation.policy_schedule_entries)
  ) {
    return null
  }
  const entry = policy.quotation.policy_schedule_entries.find(function(item) {
    return item && item.dependant && item.dependant.relationship === 'Principal'
  })
  if (!entry || !entry.dependant) {
    return null
  }
  const dependant = entry.dependant
  const name = [
    dependant.first_name,
    dependant.middle_name,
    dependant.last_name
  ]
    .filter(Boolean)
    .join(' ')
  return name.trim() ? name : null
}

// Member policies of a group, with the two approval actions GRP-060 asks for:
// one button that approves every eligible policy at once, and a per-row button
// that approves them individually.
class GroupPolicies extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      // guid -> the policy object itself, not just a flag. The table is
      // paginated, so a row ticked on page 1 is gone from props.policies by
      // the time the user approves from page 2; keeping the object means a
      // selection spanning pages can still be submitted.
      selected: {},
      confirmAllOpen: false,
      // Rejection always needs a reason, so it goes through a prompt rather
      // than firing on click. `rejectTarget` is null when the prompt is shut,
      // 'all' for the whole group, or an array of policies otherwise.
      rejectTarget: null,
      rejectReason: ''
    }
    this.handleApproveSelected = this.handleApproveSelected.bind(this)
    this.handleApproveAllInGroup = this.handleApproveAllInGroup.bind(this)
    this.handleChangePage = this.handleChangePage.bind(this)
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
    this.toggleAllOnPage = this.toggleAllOnPage.bind(this)
    this.closeReject = this.closeReject.bind(this)
    this.submitReject = this.submitReject.bind(this)
  }

  openReject(target) {
    this.setState({ rejectTarget: target, rejectReason: '' })
  }

  closeReject() {
    this.setState({ rejectTarget: null, rejectReason: '' })
  }

  submitReject() {
    const group = this.props.group
    const target = this.state.rejectTarget
    const reason = this.state.rejectReason.trim()
    if (!group || !target || !reason) {
      return
    }
    const base = {
      guid: group.guid,
      decision: 'reject',
      reason,
      page: this.props.page || 1,
      limit: this.rowsPerPage()
    }
    this.props.approvePolicies(
      target === 'all'
        ? Object.assign({ scope: 'all' }, base)
        : Object.assign({ policies: target }, base)
    )
    this.setState({ rejectTarget: null, rejectReason: '', selected: {} })
  }

  componentDidMount() {
    this.fetch(1, this.props.limit || DEFAULT_ROWS_PER_PAGE)
  }

  componentDidUpdate(prevProps) {
    // Approving a group issues its members' policies in the background. When
    // that finishes the list on screen is stale — reload it rather than making
    // the user hunt for a refresh button.
    const finished =
      prevProps.issue_policies_state === 'issuing' &&
      this.props.issue_policies_state === 'registered'
    if (finished) {
      this.fetch(1, this.rowsPerPage())
    }
  }

  fetch(page, limit) {
    const group = this.props.group
    if (!group) {
      return
    }
    this.props.getPolicies({ guid: group.guid, page, limit })
  }

  // MUI's control is 0-based; the endpoint's `page` is 1-based.
  handleChangePage(_event, zeroBasedPage) {
    this.fetch(zeroBasedPage + 1, this.rowsPerPage())
  }

  handleChangeRowsPerPage(event) {
    // Changing page size invalidates the current offset — go back to page 1.
    this.fetch(1, Number(event.target.value))
  }

  rowsPerPage() {
    return this.props.limit || DEFAULT_ROWS_PER_PAGE
  }

  policies() {
    return Array.isArray(this.props.policies) ? this.props.policies : []
  }

  // Everything ticked, across every page visited — filtered to what is still
  // actually approvable so a stale tick can never submit an ineligible policy.
  selectedPolicies() {
    const selected = this.state.selected
    return approvablePolicies(
      Object.keys(selected).map(function(guid) {
        return selected[guid]
      })
    )
  }

  // Ticked rows that are on the page currently displayed.
  selectedOnPage() {
    const selected = this.state.selected
    return approvablePolicies(this.policies()).filter(function(policy) {
      return !!selected[policy.guid]
    })
  }

  toggleOne(policy) {
    const selected = Object.assign({}, this.state.selected)
    if (selected[policy.guid]) {
      delete selected[policy.guid]
    } else {
      selected[policy.guid] = policy
    }
    this.setState({ selected })
  }

  // The header checkbox acts on this page only — the rows it can see.
  toggleAllOnPage() {
    const eligible = approvablePolicies(this.policies())
    const selected = Object.assign({}, this.state.selected)
    const allTicked = this.selectedOnPage().length === eligible.length
    eligible.forEach(function(policy) {
      if (allTicked) {
        delete selected[policy.guid]
      } else {
        selected[policy.guid] = policy
      }
    })
    this.setState({ selected })
  }

  approve(policies) {
    const group = this.props.group
    if (!group || !policies.length) {
      return
    }
    this.props.approvePolicies({
      guid: group.guid,
      policies,
      page: this.props.page || 1,
      limit: this.rowsPerPage()
    })
  }

  handleApproveSelected() {
    this.approve(this.selectedPolicies())
    this.setState({ selected: {} })
  }

  // Whole-group approval. Deliberately NOT "everything on this page": the saga
  // re-reads every page so the pages the user never looked at are included.
  handleApproveAllInGroup() {
    const group = this.props.group
    this.setState({ confirmAllOpen: false, selected: {} })
    if (!group) {
      return
    }
    this.props.approvePolicies({
      guid: group.guid,
      scope: 'all',
      page: this.props.page || 1,
      limit: this.rowsPerPage()
    })
  }

  renderProgress() {
    const total = this.props.approve_policies_total || 0
    const done = (this.props.approve_policies_results || []).length
    if (!this.props.approve_policies_loader || !total) {
      return null
    }
    const value = Math.round((done / total) * 100)
    return (
      <Box mb={2}>
        <p style={labelStyle}>
          {this.props.approve_policies_decision === 'reject'
            ? 'Rejecting'
            : 'Approving'}{' '}
          policy {Math.min(done + 1, total)} of {total}…
        </p>
        <LinearProgress
          variant="determinate"
          value={value}
          sx={{ mt: 1, borderRadius: '4px' }}
        />
      </Box>
    )
  }

  // Once a run closes, show the outcome — and list every failure with the
  // server's own reason, so a partial run is actionable rather than mysterious.
  renderResults() {
    const results = this.props.approve_policies_results || []
    if (!this.props.approve_policies_success || !results.length) {
      return null
    }
    const failures = results.filter(function(result) {
      return !result.ok
    })
    return (
      <Box mb={2}>
        <Alert
          severity={failures.length ? 'warning' : 'success'}
          style={{ borderRadius: '8px' }}
          onClose={this.props.resetApprovePolicies}
        >
          {summaryMessage(results, this.props.approve_policies_decision)}
        </Alert>
        {failures.length > 0 && (
          <Box mt={1} p={1.5} style={{ background: '#FDF6F6' }}>
            {failures.map(function(failure, index) {
              return (
                <p
                  key={index}
                  style={Object.assign({}, labelStyle, { color: '#8F191C' })}
                >
                  <strong>{failure.label}</strong>: {safeText(failure.error)}
                </p>
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  render() {
    const policies = this.policies()
    const eligible = approvablePolicies(policies)
    const selected = this.selectedPolicies()
    const selectedHere = this.selectedOnPage()
    const busy = !!this.props.approve_policies_loader
    const rejectCount = Array.isArray(this.state.rejectTarget)
      ? this.state.rejectTarget.length
      : 0
    // Approving a policy is the same decision as on the policy-processing
    // header, so it takes the same permission.
    const canApprove = hasAuthourity('APPROVE_POLICY')

    if (this.props.loader) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          p={6}
        >
          <CircularProgress style={{ color: '#032A37' }} />
          <span style={labelStyle}>Loading member policies…</span>
        </Box>
      )
    }

    return (
      <div style={detailCard}>
        <CardHeader
          title="Member Policies"
          action={
            canApprove && (this.props.total || policies.length) > 0 ? (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {selected.length > 0 && (
                  <>
                    <ButtonInc
                      variant="contained"
                      onClick={this.handleApproveSelected}
                      loading={busy}
                      disabled={busy}
                    >
                      {`Approve Selected (${selected.length})`}
                    </ButtonInc>
                    <ButtonInc
                      style={{ background: '#8F191C' }}
                      onClick={function() {
                        this.openReject(selected)
                      }.bind(this)}
                      disabled={busy}
                    >
                      {`Reject Selected (${selected.length})`}
                    </ButtonInc>
                  </>
                )}
                <ButtonInc
                  btnType={selected.length > 0 ? 'secondary' : undefined}
                  variant="contained"
                  onClick={function() {
                    this.setState({ confirmAllOpen: true })
                  }.bind(this)}
                  disabled={busy}
                >
                  Approve All in Group
                </ButtonInc>
                <ButtonInc
                  style={{ background: '#8F191C' }}
                  onClick={function() {
                    this.openReject('all')
                  }.bind(this)}
                  disabled={busy}
                >
                  Reject All in Group
                </ButtonInc>
              </Box>
            ) : null
          }
        />

        {this.props.error && (
          <Alert severity="error" style={{ borderRadius: '8px' }}>
            {safeText(this.props.error)}
          </Alert>
        )}
        {this.props.approve_policies_error && (
          <Alert
            severity="error"
            style={{ borderRadius: '8px', marginBottom: '16px' }}
          >
            {safeText(this.props.approve_policies_error)}
          </Alert>
        )}

        {this.renderProgress()}
        {this.renderResults()}

        {policies.length === 0 ? (
          <p style={labelStyle}>
            No member policies found for this group. Members and their
            dependants are onboarded after the group is registered.
          </p>
        ) : (
          <>
            {!canApprove && (
              <p style={Object.assign({}, labelStyle, { marginBottom: 12 })}>
                You do not have permission to approve policies.
              </p>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      {/* Scoped to this page: it can only tick rows it can
                          see. Whole-group approval is the separate button. */}
                      <Checkbox
                        disabled={busy || !canApprove || !eligible.length}
                        checked={
                          eligible.length > 0 &&
                          selectedHere.length === eligible.length
                        }
                        indeterminate={
                          selectedHere.length > 0 &&
                          selectedHere.length < eligible.length
                        }
                        onChange={this.toggleAllOnPage}
                      />
                    </TableCell>
                    <TableCell>Policy Number</TableCell>
                    <TableCell>Principal</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Approval Stage</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.map(
                    function(policy) {
                      const approvable = isPolicyApprovable(policy)
                      return (
                        <TableRow key={policy.guid} hover>
                          <TableCell padding="checkbox">
                            <Tooltip
                              title={
                                approvable
                                  ? ''
                                  : `Only ${APPROVABLE_STATUS} policies can be approved`
                              }
                            >
                              <span>
                                <Checkbox
                                  disabled={!approvable || busy || !canApprove}
                                  checked={!!this.state.selected[policy.guid]}
                                  onChange={function() {
                                    this.toggleOne(policy)
                                  }.bind(this)}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <span
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '12px'
                              }}
                            >
                              {policyLabel(policy)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span style={valueStyle}>
                              {safeText(principalName(policy))}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusPill status={policy.status} />
                          </TableCell>
                          <TableCell>
                            <span style={labelStyle}>{stageLabel(policy)}</span>
                          </TableCell>
                          <TableCell>
                            <span style={labelStyle}>
                              {formatDateLocale(policy.created_at) || '—'}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              gap={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                            >
                              {/* Always available — QA needs to open the
                                  policy whatever state it is in. */}
                              <ButtonInc
                                btnType="secondary"
                                onClick={function() {
                                  hashHistory.push(
                                    `admin/policy-processing/${policy.policy_number}`
                                  )
                                }}
                              >
                                View Policy
                              </ButtonInc>
                              {canApprove && approvable && (
                                <ButtonInc
                                  disabled={busy}
                                  onClick={function() {
                                    this.approve([policy])
                                  }.bind(this)}
                                >
                                  Approve
                                </ButtonInc>
                              )}
                              {canApprove && approvable && (
                                <ButtonInc
                                  style={{ background: '#8F191C' }}
                                  disabled={busy}
                                  onClick={function() {
                                    this.openReject([policy])
                                  }.bind(this)}
                                >
                                  Reject
                                </ButtonInc>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    }.bind(this)
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              {/* Selection survives paging, so say so — otherwise a count
                  higher than the rows on screen looks like a bug. */}
              <p style={labelStyle}>
                {selected.length > 0
                  ? `${selected.length} selected across all pages`
                  : `${eligible.length} of ${policies.length} on this page awaiting approval`}
              </p>
              <TablePagination
                component="div"
                count={this.props.total || policies.length}
                page={Math.max((this.props.page || 1) - 1, 0)}
                onPageChange={this.handleChangePage}
                rowsPerPage={this.rowsPerPage()}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={this.handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page"
              />
            </Box>
          </>
        )}

        <Dialog
          open={this.state.confirmAllOpen}
          onClose={function() {
            this.setState({ confirmAllOpen: false })
          }.bind(this)}
          PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
        >
          <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
            Approve every policy in this group?
          </DialogTitle>
          <DialogContent>
            <span style={{ fontFamily: 'Open Sans', fontSize: '13px' }}>
              This approves every member policy in the group that is awaiting
              approval — including policies on pages you have not opened. There
              are {this.props.total || policies.length} member policies in
              total. Policies already approved or otherwise ineligible are
              skipped.
            </span>
          </DialogContent>
          <DialogActions style={{ padding: '16px' }}>
            <ButtonInc
              btnType="secondary"
              onClick={function() {
                this.setState({ confirmAllOpen: false })
              }.bind(this)}
            >
              Cancel
            </ButtonInc>
            <ButtonInc
              variant="contained"
              onClick={this.handleApproveAllInGroup}
            >
              Approve All
            </ButtonInc>
          </DialogActions>
        </Dialog>

        {/* Rejection prompt. A reason is mandatory — the maker-checker
            decisions endpoint refuses an empty one, and on the plain status
            path the note is the only record of why the policy was turned
            down, so the submit stays disabled until one is typed. */}
        <Dialog
          open={!!this.state.rejectTarget}
          onClose={this.closeReject}
          fullWidth
          maxWidth="sm"
          PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
        >
          <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
            {this.state.rejectTarget === 'all'
              ? 'Reject every policy in this group'
              : rejectCount === 1
              ? 'Reject this policy'
              : `Reject ${rejectCount} policies`}
          </DialogTitle>
          <DialogContent>
            <p style={Object.assign({}, labelStyle, { marginBottom: 12 })}>
              {this.state.rejectTarget === 'all'
                ? 'This rejects every member policy awaiting approval, including policies on pages you have not opened. The reason is recorded against each one.'
                : 'The reason is recorded against each policy.'}
            </p>
            <TextField
              label="Reason for rejection"
              value={this.state.rejectReason}
              onChange={function(e) {
                this.setState({ rejectReason: e.target.value })
              }.bind(this)}
              multiline
              rows={4}
              fullWidth
              size="small"
              autoFocus
            />
          </DialogContent>
          <DialogActions style={{ padding: '16px' }}>
            <ButtonInc btnType="secondary" onClick={this.closeReject}>
              Cancel
            </ButtonInc>
            <ButtonInc
              style={{ background: '#8F191C' }}
              onClick={this.submitReject}
              disabled={!this.state.rejectReason.trim()}
            >
              Reject
            </ButtonInc>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

export default GroupPolicies
